import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, CloudOff, Loader2 } from "lucide-react";
import { formatApiError } from "@/lib/api";

const typing = () => {
  const el = document.activeElement;
  return !!el && (el.tagName === "TEXTAREA" || (el.tagName === "INPUT" && !["checkbox", "radio", "button"].includes(el.type)));
};

// Saves `value` as soon as the user leaves an edited cell; switches/checkboxes/add-remove save after a short pause.
export const useAutoSave = (value, save) => {
  const latest = useRef(value);
  latest.current = value;
  const saveRef = useRef(save);
  saveRef.current = save;
  const dirty = useRef(false);
  const running = useRef(null);
  const [status, setStatus] = useState("saved");

  const flush = useCallback(async () => {
    if (running.current) await running.current;
    if (!dirty.current) return;
    dirty.current = false;
    setStatus("saving");
    running.current = (async () => {
      try {
        await saveRef.current(latest.current);
        setStatus(dirty.current ? "unsaved" : "saved");
      } catch (e) {
        dirty.current = true;
        setStatus("error");
        toast.error(formatApiError(e));
      }
    })();
    await running.current;
    running.current = null;
  }, []);

  useEffect(() => {
    if (!dirty.current) return undefined;
    setStatus("unsaved");
    const t = setTimeout(() => { if (!typing()) flush(); }, 600);
    return () => clearTimeout(t);
  }, [value, flush]);

  useEffect(() => () => { flush(); }, [flush]);

  const onBlurCapture = (e) => {
    if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) setTimeout(flush, 30);
  };
  const markDirty = () => { dirty.current = true; };
  return { markDirty, onBlurCapture, status, flush };
};

const LOOK = {
  saved: ["All changes saved", "text-emerald-300 border-emerald-500/40", Check],
  saving: ["Saving...", "text-slate-300 border-slate-600", Loader2],
  unsaved: ["Unsaved edit", "text-amber-300 border-amber-500/40", Loader2],
  error: ["Not saved, click to retry", "text-rose-300 border-rose-500/50", CloudOff],
};

export const SaveStatus = ({ status, onRetry, testId = "autosave-status" }) => {
  const [text, cls, Icon] = LOOK[status];
  return (
    <button type="button" onClick={onRetry} data-testid={testId} data-status={status}
      className={`inline-flex items-center gap-1.5 h-9 px-3 rounded border text-xs font-mono transition-colors ${cls}`}>
      <Icon className={`h-3.5 w-3.5 ${status === "saving" ? "animate-spin" : ""}`} />{text}
    </button>
  );
};
